import { memo, useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import { ChevronRightIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Popover, PopoverTrigger, PopoverContent } from '../Popover';
import { menuItemActiveStateClasses } from './MenuItem';

const SUBMENU_CLOSE_DELAY = 100;

/** Why a submenu open/close was requested — hover moves are advisory, clicks and dismissals are explicit. */
export type MenuItemGroupOpenChangeReason = 'hover' | 'click' | 'dismiss';

const menuItemColors = cva(
  'flex items-center rounded-sm w-full min-h-9 gap-2 px-2.5 py-2 font-body-md font-semibold transition-all cursor-pointer disabled:cursor-not-allowed outline-none',
  {
    variants: {
      color: {
        default: [
          'text-text-primary fill-icon-primary',
          // The trigger keeps its hover look while its submenu is open (Radix sets data-state), so
          // moving the pointer into the submenu doesn't drop the highlight off the item it belongs to.
          'hover:bg-bg-sur-secondary data-[state=open]:bg-bg-sur-secondary active-self:bg-bg-inter-secondary-hover focus-visible:bg-bg-inter-secondary-hover',
          'disabled:bg-bg-inter-tertiary-default disabled:text-text-disabled disabled:text-text-disabled disabled:fill-icon-disabled',
          'dark:hover:bg-white/20 dark:active-self:bg-white/40 dark:focus-visible:bg-white/60',
          'dark:text-white dark:fill-icon-white',
          menuItemActiveStateClasses,
        ].join(' '),
      },
    },
    defaultVariants: {
      color: 'default',
    },
  },
);

export interface MenuItemGroupProps {
  label: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /** Highlights the trigger like an active MenuItem, e.g. when what the submenu edits is set. */
  active?: boolean;
  /** Drops the trailing chevron, for a lone submenu item in a menu of plain actions. */
  hideChevron?: boolean;
  contentClassName?: string;
  /** Controls the submenu. Pair with onOpenChange; leave undefined for self-managed hover/click behavior. */
  open?: boolean;
  /**
   * Every open/close *intent* with why it happened, so a controlling parent can apply its own policy
   * (e.g. ignore hover-away while the user is mid-input, keep one sibling open at a time).
   */
  onOpenChange?: (open: boolean, reason: MenuItemGroupOpenChangeReason) => void;
}

const MenuItemGroupBase = ({
  label,
  icon,
  children,
  disabled,
  active,
  hideChevron,
  className,
  contentClassName,
  open: controlledOpen,
  onOpenChange,
}: MenuItemGroupProps) => {
  const [localOpen, setLocalOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : localOpen;

  const changeOpen = (nextOpen: boolean, reason: MenuItemGroupOpenChangeReason) => {
    if (!isControlled) setLocalOpen(nextOpen);
    onOpenChange?.(nextOpen, reason);
  };

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    changeOpen(true, 'hover');
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      changeOpen(false, 'hover');
    }, SUBMENU_CLOSE_DELAY);
  };

  // Hover reveals the submenu (Linear-style), but the trigger must also open on click — otherwise it's
  // unusable without a mouse (touch, keyboard-triggered synthetic clicks) since there's no other way in.
  const handleClick = () => {
    clearCloseTimeout();
    changeOpen(!open, 'click');
  };

  // Radix's own dismissals (outside pointer-down, escape) — explicit, so they beat any hover policy.
  const handleRadixOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) changeOpen(false, 'dismiss');
  };

  return (
    <Popover open={open} onOpenChange={handleRadixOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(menuItemColors({ color: 'default' }), 'justify-between', className)}
          data-active={active}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            {icon}
            {label}
          </div>
          {hideChevron ? null : <ChevronRightIcon className="size-5 text-text-tertiary" />}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        alignOffset={-4}
        className={cn('p-1.5 z-[302] w-auto min-w-48', contentClassName)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export const MenuItemGroup = memo(MenuItemGroupBase);
